import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { AppShell } from './layout/AppShell';
import { NotFoundPage } from './NotFoundPage';
import { ShellContainer } from './ShellContainer';
import {
  ForgotPasswordPage,
  InvitePage,
  LoginPage,
  RequireAuth,
  ResetPasswordPage,
  SignupPage,
  VerifyEmailPage,
} from '@/features/auth';
import {
  AuditPage,
  AutomationsPage,
  IntegrationsPage,
  MembersPage,
  NotificationSettingsPage,
  ProfilePage,
  SettingsLayout,
  UsagePage,
  TeamsPage,
  WorkspacePage,
  CalendarSettingsPage,
} from '@/features/settings';
import { LabelsPage } from '@/features/settings/LabelsPage';
import { PersonProfilePage } from '@/features/people';
import { TodosPage } from '@/features/todos';
import { ChatPage } from '@/features/chat';
import {
  ProjectLayout,
  ProjectOverviewPage,
  ProjectSettingsPage,
  ProjectsPage,
} from '@/features/projects';
import { AllProjectsBoard, BoardView, TaskDetailPage, TaskListView } from '@/features/tasks';
import { NoteEditorPage, NotesPage, ProjectDocsPage } from '@/features/notes';
import { LeavePage, TimesheetPage } from '@/features/time';
import { MyAttendancePage, TeamAttendancePage } from '@/features/attendance';
import { DashboardPage, ProjectDashboardPage, ReportPage } from '@/features/reports';
import { PlatformPage } from '@/features/platform';
import {
  CalendarPage,
  InboxPage,
  MyDayPage,
  SprintPlanningPage,
  TimelinePage,
  WorkloadPage,
} from '@/features/views';

/**
 * Route map — spec §8.1. Public auth routes, then everything else behind
 * `RequireAuth`.
 *
 * V3 §A definition of done: no route renders a placeholder. Every screen here
 * renders live data, and the 404 is a real NotFound rather than a stub.
 */
const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/invite/:token', element: <InvitePage /> },
  { path: '/signup', element: <SignupPage /> },
  { path: '/verify-email/:token', element: <VerifyEmailPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },

  {
    element: <RequireAuth />,
    children: [
      {
        path: '/',
        element: <ShellContainer />,
        children: [
          { index: true, element: <Navigate to="/my-day" replace /> },
          { path: 'my-day', element: <MyDayPage /> },
          { path: 'inbox', element: <InboxPage /> },
          { path: 'my-tasks', element: <TaskListView scope="mine" /> },
          // V6-03 — one board across every project the viewer can see.
          { path: 'board', element: <AllProjectsBoard /> },
          { path: 'todos', element: <TodosPage /> },
          { path: 'chat', element: <ChatPage /> },
          { path: 'tasks/:key', element: <TaskDetailPage /> },
          { path: 'notes', element: <NotesPage /> },
          { path: 'notes/:id', element: <NoteEditorPage /> },
          { path: 'timesheet', element: <TimesheetPage /> },
          { path: 'leave', element: <LeavePage /> },
          // V6-04 — mine, and the team report beside it.
          { path: 'attendance', element: <MyAttendancePage /> },
          { path: 'attendance/team', element: <TeamAttendancePage /> },
          { path: 'projects', element: <ProjectsPage /> },
          // A layout route, so the project tab bar renders once above whichever
          // screen is showing. Settings and Overview were unreachable from
          // anywhere in the UI until it existed.
          {
            path: 'projects/:key',
            element: <ProjectLayout />,
            children: [
              // The sidebar still lands on the board; the project grid lands on
              // Overview, because that is the "what is this?" entry point.
              { index: true, element: <Navigate to="board" replace /> },
              { path: 'overview', element: <ProjectOverviewPage /> },
              { path: 'board', element: <BoardView /> },
              { path: 'list', element: <TaskListView /> },
              { path: 'calendar', element: <CalendarPage /> },
              { path: 'timeline', element: <TimelinePage /> },
              { path: 'workload', element: <WorkloadPage scope="project" /> },
              { path: 'docs', element: <ProjectDocsPage /> },
              { path: 'docs/:noteId', element: <NoteEditorPage inProject /> },
              { path: 'sprints', element: <SprintPlanningPage /> },
              { path: 'dashboard', element: <ProjectDashboardPage /> },
              { path: 'settings', element: <ProjectSettingsPage /> },
            ],
          },
          { path: 'people/:userId', element: <PersonProfilePage /> },
          { path: 'workload', element: <WorkloadPage /> },
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'platform', element: <PlatformPage /> },
          { path: 'reports', element: <ReportPage /> },
          { path: 'reports/:name', element: <ReportPage /> },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <Navigate to="/settings/profile" replace /> },
              { path: 'profile', element: <ProfilePage /> },
              { path: 'members', element: <MembersPage /> },
              { path: 'teams', element: <TeamsPage /> },
              { path: 'workspace', element: <WorkspacePage /> },
              { path: 'notifications', element: <NotificationSettingsPage /> },
              { path: 'calendar', element: <CalendarSettingsPage /> },
              { path: 'labels', element: <LabelsPage /> },
              { path: 'automations', element: <AutomationsPage /> },
              { path: 'integrations', element: <IntegrationsPage /> },
              { path: 'usage', element: <UsagePage /> },
              { path: 'audit', element: <AuditPage /> },
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export { AppShell };
